import { Injectable, inject } from '@angular/core';

import { OfferingMilestoneSdk } from '@libs/graphql/sdk/offering-milestone/offering-milestone.sdk';
import { OfferingMilestoneShareSdk } from '@libs/graphql/sdk/offering-milestone-share/offering-milestone-share.sdk';
import type { OfferingMilestonePoco, OfferingMilestoneSharePoco } from '@libs/graphql/models';
import type { FullPackageAllocation } from '@shared/utils/full-package.util';

/**
 * Loads the deposit split configured on a set of packages, for the staleness check in
 * `blockedFullPackageIds`.
 *
 * It lives in shared because FOUR buyer surfaces have to reach the same verdict — /vendor/services,
 * the customer vendor page, the customer booking form and the vendor New Booking wizard. When
 * those checks are written per-loader they drift, and a package ends up bookable on one screen and
 * hidden on another. One query, one shape, five callers.
 *
 * Never throws: a package whose allocation can't be read is treated as having none, which means
 * "not blocked by allocation". Over-hiding a sellable package on a transient read failure would be
 * worse than the check being skipped for one page load.
 */
@Injectable({ providedIn: 'root' })
export class FullPackageAllocationService {
  private readonly milestoneSdk = inject(OfferingMilestoneSdk);
  private readonly shareSdk = inject(OfferingMilestoneShareSdk);

  async load(offeringIds: number[]): Promise<FullPackageAllocation> {
    const empty: FullPackageAllocation = { milestones: [], shares: [] };
    if (!offeringIds.length) return empty;
    try {
      const msRes = await this.milestoneSdk.list(
        { offeringId: { in: offeringIds } }, [{ column: 'sortOrder', desc: false }],
        // `sortOrder` is not decoration: 'balance' and 'Pay up to %' are both relative to the
        // phases above them, so what a phase collects cannot be worked out without the order.
        ['id', 'offeringId', 'offeringPackageId', 'mode', 'value', 'sortOrder'], 1, 5000,
      );
      const milestones = (msRes.data as OfferingMilestonePoco[]) ?? [];
      if (!milestones.length) return empty;

      const shRes = await this.shareSdk.list(
        { offeringMilestoneId: { in: milestones.map(m => m.id) } }, [{ column: 'id', desc: false }],
        ['id', 'offeringMilestoneId', 'packageItemId', 'mode', 'value'], 1, 20000,
      );
      return { milestones, shares: (shRes.data as OfferingMilestoneSharePoco[]) ?? [] };
    } catch (err) {
      console.warn('deposit split unavailable — allocation staleness not checked', err);
      return empty;
    }
  }
}
